import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { authApi, type UserView } from '../api/auth'
import { onUnauthenticated, session } from '../api/client'
import { MINE, setOrgSelection } from './useOrg'

// The signed-in person is ONE hook, like the spoke. Pages read `user`; the shell reads `status`.
interface SessionState {
  status: 'loading' | 'anonymous' | 'signed-in'
  user: UserView | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Re-reads the person from the API. Used after a password change, which clears their hold. */
  refresh: () => Promise<void>
}

const SessionContext = createContext<SessionState | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>(session.get() ? 'loading' : 'anonymous')
  const [user, setUser] = useState<UserView | null>(null)

  useEffect(() => {
    onUnauthenticated.handler = () => {
      session.set(null)
      setUser(null)
      setStatus('anonymous')
    }
    return () => {
      onUnauthenticated.handler = null
    }
  }, [])

  useEffect(() => {
    if (!session.get()) return
    let cancelled = false
    authApi
      .me()
      .then((u) => {
        if (cancelled) return
        setUser(u)
        setStatus('signed-in')
      })
      .catch(() => {
        if (cancelled) return
        session.set(null)
        setStatus('anonymous')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    session.set(res.token)
    // you open on YOUR org (owner, 2026-10-12: "it should be whichever organization that is yours which is selected by
    // default"): a pick made in an earlier sitting, or by whoever signed in on this browser before, does not carry over
    setOrgSelection(MINE)
    setUser(res.user)
    setStatus('signed-in')
  }, [])

  const signOut = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      session.set(null)
      setOrgSelection(MINE)   // the next person to sign in here opens on their own org, not on this one's pick
      setUser(null)
      setStatus('anonymous')
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!session.get()) return
    const me = await authApi.me()
    setUser(me)
    setStatus('signed-in')
  }, [])

  const value = useMemo(() => ({ status, user, signIn, signOut, refresh }), [status, user, signIn, signOut, refresh])
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}
